import { BarRounded } from '@visx/shape';
import type { AxisScale } from '@visx/xychart';

import {
  BAR_STACKED_GAP_LINE_COLOR,
  BAR_STACKED_GAP_LINE_WIDTH_PX,
  BAR_STACKED_MIN_SEGMENT_HEIGHT_FOR_GAP,
  BAR_STACKED_TOP_RADIUS_PX,
} from '../../constants';

import type { BarsProps } from './types';

/**
 * Custom Bars component - matches visx implementation.
 * Features:
 * - 1.5px gap line at top of stacked bar segments (not full stroke that would hide thin bars)
 * - Minimum 2px height for bars with values > 0 (planned)
 * - Per-bar border radius based on isTopBar flag (for proper radius on bars where upper series have no data)
 */
export function Bars({
  bars,
  horizontal,
  xScale,
  yScale,
  showStrokeGap = false,
  radius,
  radiusAll,
  radiusTop,
  radiusRight,
  radiusBottom,
  radiusLeft,
  ...restProps
}: BarsProps<AxisScale, AxisScale>) {
  const isFocusable = Boolean(restProps.onFocus || restProps.onBlur);

  return (
    <>
      {bars.map(({ key, originalHeight, stackOffset, isTopBar, ...barProps }) => {
        const { x, y, width, height } = barProps;
        const segmentHeight = originalHeight ?? height;
        const showGapLine = showStrokeGap && segmentHeight >= BAR_STACKED_MIN_SEGMENT_HEIGHT_FOR_GAP;
        // Determine if this bar should have rounded corners
        // Use per-bar isTopBar flag if available, otherwise fall back to component-level radius props
        const shouldRoundTop = isTopBar || (radius !== undefined && radius !== null);
        // Scale radius quadratically with bar width so narrow bars get much less rounding.
        // e.g. 8px wide → 1.8px, 10px → 2.9px, 12px+ → 4px cap.
        const scaledTopRadius = Math.min((width * width) / 35, BAR_STACKED_TOP_RADIUS_PX);
        const barRadius = isTopBar ? scaledTopRadius : (radius as number | undefined);

        return (
          <g key={key}>
            {/* Bar shape */}
            {!shouldRoundTop ? (
              <rect className="visx-bar" tabIndex={isFocusable ? 0 : undefined} {...barProps} {...restProps} />
            ) : (
              <BarRounded
                className="visx-bar"
                tabIndex={isFocusable ? 0 : undefined}
                radius={barRadius ?? scaledTopRadius}
                all={radiusAll as boolean}
                top={isTopBar ? true : (radiusTop as boolean)}
                right={radiusRight as boolean}
                bottom={radiusBottom as boolean}
                left={radiusLeft as boolean}
                {...barProps}
                {...restProps}
              />
            )}
            {/* Gap line only when this segment is >= 3px */}
            {showGapLine && (
              <line
                x1={x}
                y1={y}
                x2={x + width}
                y2={y}
                stroke={BAR_STACKED_GAP_LINE_COLOR}
                strokeWidth={BAR_STACKED_GAP_LINE_WIDTH_PX}
              />
            )}
          </g>
        );
      })}
    </>
  );
}
